import React, { useCallback, useEffect, useRef } from 'react'

import { logAndCaptureException } from '../utils/errorTools'
import useIsSsr from './useIsSsr'

export interface BoxSize {
  width: number
  height: number
}

const borderBoxSizeFrom = (entry: ResizeObserverEntry | undefined): BoxSize | undefined => {
  if (!entry) {
    return undefined
  }
  if (entry.borderBoxSize) {
    const { blockSize: height, inlineSize: width } = entry.borderBoxSize[0] ?? {}
    return { width, height }
  }
  if (!entry.contentRect) {
    return undefined
  }
  // Safari only supports `contentRect`
  const { width, height } = entry.contentRect
  return { width, height }
}

export interface BoxSizeCallback {
  (size: BoxSize | undefined): void
}

export const useResizeObserver = (callback: BoxSizeCallback): React.RefCallback<HTMLElement> => {
  const lastObservedRef = useRef<HTMLElement>()
  const callbackRef = useRef<BoxSizeCallback>(callback)
  const isSSR = useIsSsr()

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  const observerRef = useRef<ResizeObserver>()

  useEffect((): void | (() => void) => {
    if (isSSR) {
      return
    }
    try {
      observerRef.current = new ResizeObserver(([entry]) => {
        const borderBoxSize = borderBoxSizeFrom(entry)
        if (!borderBoxSize) {
          return
        }
        callbackRef.current(borderBoxSize)
      })
      // in case we missed it (re-render, SSR, etc.)
      if (lastObservedRef.current) {
        observerRef.current.observe(lastObservedRef.current)
      }
      return () => {
        observerRef.current?.disconnect()
      }
    } catch (err: unknown) {
      logAndCaptureException(err instanceof Error ? err : new Error(`${err}`))
    }
  }, [isSSR])

  return useCallback((element) => {
    const lastObserved = lastObservedRef.current
    if (lastObserved) {
      observerRef.current?.unobserve(lastObserved)
      lastObservedRef.current = undefined
    }

    if (element == null) {
      return
    }

    observerRef.current?.observe(element)
    lastObservedRef.current = element
  }, [])
}
